/**
 * The forms of the CRM: what each one holds, how it becomes a request, and its
 * fields — one set per entity, rendered by the create dialog and the edit
 * dialog alike, so the two cannot drift.
 *
 * An edit sends **only what changed**. The server records the changed fields
 * in the audit log; posting the untouched ones back would make every save look
 * like a no-op diff of everything.
 */
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  ACTIVITY_TYPES,
  CRM_DEFAULT_CURRENCY,
  CRM_LIMITS,
  type ActivityDto,
  type ActivityType,
  type CreateActivityRequest,
  type CreateDealRequest,
  type CreateOrganizationRequest,
  type CreatePersonRequest,
  type CreateStageRequest,
  type CrmRefDto,
  type DealDto,
  type OrganizationDto,
  type PersonDto,
  type StageDto,
  type UpdateActivityRequest,
  type UpdateDealRequest,
  type UpdateOrganizationRequest,
  type UpdatePersonRequest,
  type UpdateStageRequest,
} from '@forsys/shared';
import { CurrencyPicker } from '../../components/CurrencyPicker';
import { preferredFirst, useCurrencies } from '../currency/module';
import { DealPicker, OrganizationPicker, OwnerPicker, PersonPicker } from './pickers';
import { today } from './api';

type Patch<F> = (changes: Partial<F>) => void;

const sameRef = (a: CrmRefDto | null, b: CrmRefDto | null) => (a?.id ?? null) === (b?.id ?? null);
const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** A typed amount, with a decimal comma accepted. NaN when it is not a number. */
function amount(raw: string): number {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  return cleaned === '' ? 0 : Number(cleaned);
}

/** Decimals an amount is written with — what the server compares with the currency's. */
function decimalsOf(value: number): number {
  return Number.isFinite(value) ? (String(value).split('.')[1]?.length ?? 0) : 0;
}

// --- deals -------------------------------------------------------------------

export interface DealForm {
  title: string;
  value: string;
  currency: string;
  /** Create only: the stage a new deal starts in. */
  stageId: number | null;
  person: CrmRefDto | null;
  organization: CrmRefDto | null;
  expectedCloseDate: string;
  /** Null on create: the caller. */
  owner: CrmRefDto | null;
}

export const BLANK_DEAL: DealForm = {
  title: '',
  value: '',
  currency: CRM_DEFAULT_CURRENCY,
  stageId: null,
  person: null,
  organization: null,
  expectedCloseDate: '',
  owner: null,
};

export function dealFormFor(deal: DealDto): DealForm {
  return {
    title: deal.title,
    value: String(deal.value),
    currency: deal.currency,
    stageId: deal.stage.id,
    person: deal.person,
    organization: deal.organization,
    expectedCloseDate: deal.expectedCloseDate ?? '',
    owner: deal.owner,
  };
}

export function dealIncomplete(form: DealForm): boolean {
  return form.title.trim() === '' || Number.isNaN(amount(form.value));
}

export function createDealRequest(form: DealForm): CreateDealRequest {
  return {
    title: form.title.trim(),
    value: amount(form.value),
    currency: form.currency.trim() || CRM_DEFAULT_CURRENCY,
    ...(form.stageId !== null ? { stageId: form.stageId } : {}),
    ...(form.person ? { personId: form.person.id } : {}),
    ...(form.organization ? { organizationId: form.organization.id } : {}),
    ...(form.expectedCloseDate ? { expectedCloseDate: form.expectedCloseDate } : {}),
    ...(form.owner ? { ownerId: form.owner.id } : {}),
  };
}

export function dealChanges(deal: DealDto, form: DealForm): UpdateDealRequest {
  const changes: UpdateDealRequest = {};
  if (form.title.trim() !== deal.title) changes.title = form.title.trim();
  if (amount(form.value) !== deal.value) changes.value = amount(form.value);
  if (form.currency.trim().toUpperCase() !== deal.currency) changes.currency = form.currency.trim();
  if (!sameRef(form.person, deal.person)) changes.personId = form.person?.id ?? null;
  if (!sameRef(form.organization, deal.organization)) changes.organizationId = form.organization?.id ?? null;
  if ((orNull(form.expectedCloseDate) ?? null) !== deal.expectedCloseDate) {
    changes.expectedCloseDate = orNull(form.expectedCloseDate);
  }
  if (form.owner && form.owner.id !== deal.owner.id) changes.ownerId = form.owner.id;
  return changes;
}

export function DealFields({
  form,
  patch,
  stages,
}: {
  form: DealForm;
  patch: Patch<DealForm>;
  /** Offer a stage — on create. */
  stages?: readonly StageDto[];
}) {
  const { t } = useTranslation('crm');
  const currencies = useCurrencies();
  const digits = currencies.digitsOf(form.currency);
  const typed = amount(form.value);
  return (
    <>
      <TextField
        label={t('field.title')}
        value={form.title}
        onChange={(event) => patch({ title: event.target.value })}
        slotProps={{ htmlInput: { maxLength: CRM_LIMITS.nameLength } }}
        autoFocus
        fullWidth
        required
      />
      <Stack direction="row" spacing={2}>
        <TextField
          label={t('field.value')}
          value={form.value}
          onChange={(event) => patch({ value: event.target.value })}
          error={Number.isNaN(typed) || decimalsOf(typed) > digits}
          helperText={t('hint.value', { count: digits })}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          fullWidth
        />
        <Box sx={{ width: 260, flexShrink: 0 }}>
          <CurrencyPicker
            label={t('field.currency')}
            options={preferredFirst(currencies.codes, [CRM_DEFAULT_CURRENCY, form.currency])}
            value={form.currency}
            onChange={(currency) => patch({ currency: currency ?? CRM_DEFAULT_CURRENCY })}
            loading={currencies.loading}
          />
        </Box>
      </Stack>
      {stages && stages.length > 0 && (
        <TextField
          select
          label={t('field.stage')}
          value={form.stageId ?? stages[0]?.id ?? ''}
          onChange={(event) => patch({ stageId: Number(event.target.value) })}
          fullWidth
        >
          {stages.map((stage) => (
            <MenuItem key={stage.id} value={stage.id}>
              {stage.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      <OrganizationPicker value={form.organization} onChange={(organization) => patch({ organization })} />
      <PersonPicker value={form.person} onChange={(person) => patch({ person })} />
      <TextField
        type="date"
        label={t('field.expectedCloseDate')}
        value={form.expectedCloseDate}
        onChange={(event) => patch({ expectedCloseDate: event.target.value })}
        slotProps={{ inputLabel: { shrink: true } }}
        fullWidth
      />
      <OwnerPicker value={form.owner} onChange={(owner) => patch({ owner })} helperText={t('hint.ownerDefault')} />
    </>
  );
}

// --- organizations -----------------------------------------------------------

export interface OrganizationForm {
  name: string;
  address: string;
  owner: CrmRefDto | null;
}

export const BLANK_ORGANIZATION: OrganizationForm = { name: '', address: '', owner: null };

export function organizationFormFor(organization: OrganizationDto): OrganizationForm {
  return { name: organization.name, address: organization.address ?? '', owner: organization.owner };
}

export function createOrganizationRequest(form: OrganizationForm): CreateOrganizationRequest {
  return {
    name: form.name.trim(),
    ...(orNull(form.address) ? { address: orNull(form.address) } : {}),
    ...(form.owner ? { ownerId: form.owner.id } : {}),
  };
}

export function organizationChanges(organization: OrganizationDto, form: OrganizationForm): UpdateOrganizationRequest {
  const changes: UpdateOrganizationRequest = {};
  if (form.name.trim() !== organization.name) changes.name = form.name.trim();
  if (orNull(form.address) !== organization.address) changes.address = orNull(form.address);
  if (form.owner && form.owner.id !== organization.owner.id) changes.ownerId = form.owner.id;
  return changes;
}

export function OrganizationFields({ form, patch }: { form: OrganizationForm; patch: Patch<OrganizationForm> }) {
  const { t } = useTranslation('crm');
  return (
    <>
      <TextField
        label={t('field.name')}
        value={form.name}
        onChange={(event) => patch({ name: event.target.value })}
        slotProps={{ htmlInput: { maxLength: CRM_LIMITS.nameLength } }}
        autoFocus
        fullWidth
        required
      />
      <TextField
        label={t('field.address')}
        value={form.address}
        onChange={(event) => patch({ address: event.target.value })}
        slotProps={{ htmlInput: { maxLength: CRM_LIMITS.addressLength } }}
        fullWidth
      />
      <OwnerPicker value={form.owner} onChange={(owner) => patch({ owner })} helperText={t('hint.ownerDefault')} />
    </>
  );
}

// --- persons -----------------------------------------------------------------

export interface PersonForm {
  name: string;
  email: string;
  phone: string;
  organization: CrmRefDto | null;
  owner: CrmRefDto | null;
}

export const BLANK_PERSON: PersonForm = { name: '', email: '', phone: '', organization: null, owner: null };

export function personFormFor(person: PersonDto): PersonForm {
  return {
    name: person.name,
    email: person.email ?? '',
    phone: person.phone ?? '',
    organization: person.organization,
    owner: person.owner,
  };
}

export function createPersonRequest(form: PersonForm): CreatePersonRequest {
  return {
    name: form.name.trim(),
    ...(orNull(form.email) ? { email: orNull(form.email) } : {}),
    ...(orNull(form.phone) ? { phone: orNull(form.phone) } : {}),
    ...(form.organization ? { organizationId: form.organization.id } : {}),
    ...(form.owner ? { ownerId: form.owner.id } : {}),
  };
}

export function personChanges(person: PersonDto, form: PersonForm): UpdatePersonRequest {
  const changes: UpdatePersonRequest = {};
  if (form.name.trim() !== person.name) changes.name = form.name.trim();
  if (orNull(form.email) !== person.email) changes.email = orNull(form.email);
  if (orNull(form.phone) !== person.phone) changes.phone = orNull(form.phone);
  if (!sameRef(form.organization, person.organization)) changes.organizationId = form.organization?.id ?? null;
  if (form.owner && form.owner.id !== person.owner.id) changes.ownerId = form.owner.id;
  return changes;
}

export function PersonFields({ form, patch }: { form: PersonForm; patch: Patch<PersonForm> }) {
  const { t } = useTranslation('crm');
  return (
    <>
      <TextField
        label={t('field.name')}
        value={form.name}
        onChange={(event) => patch({ name: event.target.value })}
        slotProps={{ htmlInput: { maxLength: CRM_LIMITS.nameLength } }}
        autoFocus
        fullWidth
        required
      />
      <TextField
        type="email"
        label={t('field.email')}
        value={form.email}
        onChange={(event) => patch({ email: event.target.value })}
        slotProps={{ htmlInput: { maxLength: CRM_LIMITS.emailLength } }}
        fullWidth
      />
      <TextField
        type="tel"
        label={t('field.phone')}
        value={form.phone}
        onChange={(event) => patch({ phone: event.target.value })}
        slotProps={{ htmlInput: { maxLength: CRM_LIMITS.phoneLength } }}
        fullWidth
      />
      <OrganizationPicker value={form.organization} onChange={(organization) => patch({ organization })} />
      <OwnerPicker value={form.owner} onChange={(owner) => patch({ owner })} helperText={t('hint.ownerDefault')} />
    </>
  );
}

// --- activities --------------------------------------------------------------

export interface ActivityForm {
  type: ActivityType;
  subject: string;
  dueDate: string;
  dueTime: string;
  note: string;
  deal: CrmRefDto | null;
  /** On create with a deal, empty means "the deal's" — the server copies it. */
  person: CrmRefDto | null;
  organization: CrmRefDto | null;
  owner: CrmRefDto | null;
}

export function blankActivity(link: Partial<Pick<ActivityForm, 'deal' | 'person' | 'organization'>> = {}): ActivityForm {
  return {
    type: 'call',
    subject: '',
    dueDate: today(),
    dueTime: '',
    note: '',
    deal: link.deal ?? null,
    person: link.person ?? null,
    organization: link.organization ?? null,
    owner: null,
  };
}

export function activityFormFor(activity: ActivityDto): ActivityForm {
  return {
    type: activity.type,
    subject: activity.subject,
    dueDate: activity.dueDate,
    dueTime: activity.dueTime ?? '',
    note: activity.note ?? '',
    deal: activity.deal,
    person: activity.person,
    organization: activity.organization,
    owner: activity.owner,
  };
}

export function activityIncomplete(form: ActivityForm): boolean {
  return form.subject.trim() === '' || form.dueDate === '';
}

export function createActivityRequest(form: ActivityForm): CreateActivityRequest {
  return {
    type: form.type,
    subject: form.subject.trim(),
    dueDate: form.dueDate,
    ...(form.dueTime ? { dueTime: form.dueTime } : {}),
    ...(form.note.trim() ? { note: form.note } : {}),
    ...(form.deal ? { dealId: form.deal.id } : {}),
    ...(form.person ? { personId: form.person.id } : {}),
    ...(form.organization ? { organizationId: form.organization.id } : {}),
    ...(form.owner ? { ownerId: form.owner.id } : {}),
  };
}

export function activityChanges(activity: ActivityDto, form: ActivityForm): UpdateActivityRequest {
  const changes: UpdateActivityRequest = {};
  if (form.type !== activity.type) changes.type = form.type;
  if (form.subject.trim() !== activity.subject) changes.subject = form.subject.trim();
  if (form.dueDate !== activity.dueDate) changes.dueDate = form.dueDate;
  if ((form.dueTime || null) !== activity.dueTime) changes.dueTime = form.dueTime || null;
  if ((form.note.trim() ? form.note : null) !== activity.note) changes.note = form.note.trim() ? form.note : null;
  if (!sameRef(form.deal, activity.deal)) changes.dealId = form.deal?.id ?? null;
  if (!sameRef(form.person, activity.person)) changes.personId = form.person?.id ?? null;
  if (!sameRef(form.organization, activity.organization)) changes.organizationId = form.organization?.id ?? null;
  if (form.owner && form.owner.id !== activity.owner.id) changes.ownerId = form.owner.id;
  return changes;
}

export function ActivityFields({ form, patch }: { form: ActivityForm; patch: Patch<ActivityForm> }) {
  const { t } = useTranslation('crm');
  return (
    <>
      <Stack direction="row" spacing={2}>
        <TextField
          select
          label={t('field.type')}
          value={form.type}
          onChange={(event) => patch({ type: event.target.value as ActivityType })}
          sx={{ width: 160, flexShrink: 0 }}
        >
          {ACTIVITY_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`activityType.${type}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('field.subject')}
          value={form.subject}
          onChange={(event) => patch({ subject: event.target.value })}
          slotProps={{ htmlInput: { maxLength: CRM_LIMITS.nameLength } }}
          autoFocus
          fullWidth
          required
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <TextField
          type="date"
          label={t('field.dueDate')}
          value={form.dueDate}
          onChange={(event) => patch({ dueDate: event.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
          required
        />
        <TextField
          type="time"
          label={t('field.dueTime')}
          value={form.dueTime}
          onChange={(event) => patch({ dueTime: event.target.value })}
          helperText={t('hint.dueTime')}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
      </Stack>
      <DealPicker value={form.deal} onChange={(deal) => patch({ deal })} />
      <PersonPicker
        value={form.person}
        onChange={(person) => patch({ person })}
        {...(form.deal ? { helperText: t('hint.fromDeal') } : {})}
      />
      <OrganizationPicker
        value={form.organization}
        onChange={(organization) => patch({ organization })}
        {...(form.deal ? { helperText: t('hint.fromDeal') } : {})}
      />
      <TextField
        label={t('field.note')}
        value={form.note}
        onChange={(event) => patch({ note: event.target.value })}
        slotProps={{ htmlInput: { maxLength: CRM_LIMITS.noteLength } }}
        multiline
        minRows={3}
        fullWidth
      />
      <OwnerPicker value={form.owner} onChange={(owner) => patch({ owner })} helperText={t('hint.ownerDefault')} />
    </>
  );
}

// --- stages ------------------------------------------------------------------

export interface StageForm {
  name: string;
  probability: string;
  rottenDays: string;
}

export const BLANK_STAGE: StageForm = { name: '', probability: '100', rottenDays: '' };

export function stageFormFor(stage: StageDto): StageForm {
  return {
    name: stage.name,
    probability: String(stage.probability),
    rottenDays: stage.rottenDays === null ? '' : String(stage.rottenDays),
  };
}

const wholeNumber = (raw: string): number | null => (raw.trim() === '' ? null : Number(raw));

export function stageIncomplete(form: StageForm): boolean {
  const probability = Number(form.probability);
  return (
    form.name.trim() === '' ||
    !Number.isInteger(probability) ||
    (form.rottenDays.trim() !== '' && !Number.isInteger(Number(form.rottenDays)))
  );
}

export function createStageRequest(form: StageForm): CreateStageRequest {
  return {
    name: form.name.trim(),
    probability: Number(form.probability),
    rottenDays: wholeNumber(form.rottenDays),
  };
}

export function stageChanges(stage: StageDto, form: StageForm): UpdateStageRequest {
  const changes: UpdateStageRequest = {};
  if (form.name.trim() !== stage.name) changes.name = form.name.trim();
  if (Number(form.probability) !== stage.probability) changes.probability = Number(form.probability);
  if (wholeNumber(form.rottenDays) !== stage.rottenDays) changes.rottenDays = wholeNumber(form.rottenDays);
  return changes;
}

export function StageFields({ form, patch }: { form: StageForm; patch: Patch<StageForm> }) {
  const { t } = useTranslation('crm');
  return (
    <>
      <TextField
        label={t('field.name')}
        value={form.name}
        onChange={(event) => patch({ name: event.target.value })}
        slotProps={{ htmlInput: { maxLength: CRM_LIMITS.stageNameLength } }}
        autoFocus
        fullWidth
        required
      />
      <TextField
        type="number"
        label={t('field.probability')}
        value={form.probability}
        onChange={(event) => patch({ probability: event.target.value })}
        helperText={t('hint.probability')}
        slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
        fullWidth
      />
      <TextField
        type="number"
        label={t('field.rottenDays')}
        value={form.rottenDays}
        onChange={(event) => patch({ rottenDays: event.target.value })}
        helperText={t('hint.rottenDays')}
        slotProps={{ htmlInput: { min: 1, max: 3650, step: 1 } }}
        fullWidth
      />
    </>
  );
}
